import { get } from 'lodash'
import { usePlatformAuthStore } from '@/stores/platformAuth'
import { analytics } from '@/services'

export function useAuthHelpers() {
    function isAuthenticationException(e) {
        return get(e, 'response.status') === 403
            && [
                'App\\Exceptions\\CredentialMissingException',
                'App\\Exceptions\\CredentialInvalidException',
            ].includes(get(e, 'response.data.type'))
    }

    function handleForceLoginRestartIfRequired(data) {
        if (data.force_login) {
            const appStore = useAppStore()

            appStore.restart()
        }
    }

    function handleAuthenticationException(data, router, loginHandler, abortHandler = null, push = false, startUp = false) {
        const appStore = useAppStore()

        // If mock data mode is enabled we shouldn't show the login page
        if (appStore.mockData) {
            const { snackbar } = useSnackbars()
            snackbar({
                message: 'Not supported in mock data mode',
                type: 'error',
            })
            if (abortHandler) {
                abortHandler()
            }
            return
        }

        const platformAuthStore = usePlatformAuthStore()

        platformAuthStore.setAuthRequirements(data.requirements)
        platformAuthStore.setLoginSuccessHandler(loginHandler)
        platformAuthStore.setLoginAbortHandler(abortHandler)

        // Try navigating to the root and have it automatically replace with login
        // There's no going back if login is forced so navigate back to the root
        if (data.force_login) {
            // Don't allow the user to go back
            platformAuthStore.setLoginAbortHandler(null)

            // Restart the app if it's not the initial login
            // This should only happen if we check startup auth user credentials, and they're no longer valid
            // This ensures the state is cleared and the user will be presented with the login page
            // Restarting avoids issues with ionic-router and vue-router getting out of sync (e.g. URL ends up as /login, but ionic shows the dashboard)
            // We need to clear the route history so android users can't hit the back button
            // For now, restarting is a more reliable solution due to router complexities
            // Related issue: https://github.com/ionic-team/ionic-framework/issues/29721
            if (!startUp) {
                handleForceLoginRestartIfRequired(data)
                return
            }
        }

        const method = push ? router.push : router.replace

        method({ name: 'login' })
    }

    function handleUserContextChange(context) {
        const appStore = useAppStore()
        appStore.setImpersonating(context.is_impersonating ?? false)
        return analytics.setRoles(context.roles ?? [])
    }

    return {
        isAuthenticationException,
        handleAuthenticationException,
        handleUserContextChange,
        handleForceLoginRestartIfRequired,
    }
}
